// =============================================================================
// FILE: packages/renderer/src/components/capture/ScreenshotButton.tsx
// PURPOSE: Persistent "Viral Marketing" screenshot engine. A one-click camera
//          button (lower-right) captures the current dashboard, stamps it with
//          the RCD² Engine watermark, timestamp, project name, current module
//          and user, plus a scannable QR code to https://rcd2engine.com, then
//          downloads the PNG. Every shared shot becomes a portal to the product.
// =============================================================================

import { useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store.js';
import { haptic, playBeep } from '../../lib/soundManager.js';

const QR_SRC = `${import.meta.env.BASE_URL}qr-rcd2engine.png`;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function ScreenshotButton({ moduleLabel }: { moduleLabel: string }): JSX.Element {
  const userEmail = useAuthStore((s) => s.user?.email);
  const [busy, setBusy] = useState(false);

  const capture = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    haptic(12);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const target = document.querySelector('main') as HTMLElement | null ?? document.body;
      const shot = await html2canvas(target, {
        backgroundColor: '#050b14',
        scale: Math.min(2, window.devicePixelRatio || 1),
        useCORS: true,
        logging: false,
        ignoreElements: (el) => el.hasAttribute('data-noshot'),
      });

      const ctx = shot.getContext('2d');
      if (ctx) {
        const W = shot.width, H = shot.height;
        const pad = Math.round(W * 0.014);
        const barH = Math.round(H * 0.062);

        // Bottom info bar
        const grad = ctx.createLinearGradient(0, H - barH, 0, H);
        grad.addColorStop(0, 'rgba(4,9,18,0)');
        grad.addColorStop(0.35, 'rgba(4,9,18,0.85)');
        grad.addColorStop(1, 'rgba(4,9,18,0.96)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, H - barH, W, barH);

        const fs = Math.max(12, Math.round(barH * 0.26));
        const midY = H - barH / 2;

        // Watermark — RCD² Engine
        ctx.textBaseline = 'middle';
        ctx.font = `800 ${Math.round(fs * 1.15)}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = '#5eead4';
        ctx.shadowColor = 'rgba(45,212,191,0.6)';
        ctx.shadowBlur = 10;
        ctx.fillText('RCD² Engine', pad, midY - fs * 0.55);
        ctx.shadowBlur = 0;

        // Meta line: module · project · user · timestamp
        const ts = new Date().toLocaleString();
        const meta = [moduleLabel, userEmail ? userEmail.split('@')[0] : null, ts]
          .filter(Boolean).join('   ·   ');
        ctx.font = `500 ${Math.round(fs * 0.82)}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = 'rgba(200,220,225,0.85)';
        ctx.fillText(meta, pad, midY + fs * 0.7);

        // QR code — bottom-right, scannable but subtle
        try {
          const qr = await loadImage(QR_SRC);
          const qrSize = Math.round(barH * 0.82);
          const qx = W - qrSize - pad, qy = H - barH + (barH - qrSize) / 2;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(qx - 3, qy - 3, qrSize + 6, qrSize + 6);
          ctx.globalAlpha = 0.92;
          ctx.drawImage(qr, qx, qy, qrSize, qrSize);
          ctx.globalAlpha = 1;
          ctx.font = `600 ${Math.round(fs * 0.6)}px Inter, system-ui, sans-serif`;
          ctx.fillStyle = 'rgba(200,220,225,0.75)';
          ctx.textAlign = 'right';
          ctx.fillText('rcd2engine.com', qx - 8, midY);
          ctx.textAlign = 'left';
        } catch { /* QR optional */ }
      }

      const url = shot.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `RCD2-${moduleLabel.replace(/\s+/g, '-')}-${Date.now()}.png`;
      a.click();
      playBeep(880);
    } catch {
      playBeep(320);
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <button
      type="button"
      data-testid="screenshot-button"
      data-noshot
      data-nosound
      onClick={capture}
      disabled={busy}
      title="Capture a branded screenshot (with QR to rcd2engine.com)"
      style={{
        position: 'fixed', right: 72, bottom: 86, zIndex: 9500,
        width: 46, height: 46, borderRadius: '50%',
        display: 'grid', placeItems: 'center', cursor: busy ? 'wait' : 'pointer',
        background: 'rgba(6,14,24,0.94)', border: '1px solid rgba(45,212,191,0.5)',
        boxShadow: '0 8px 26px rgba(0,0,0,0.5), 0 0 20px rgba(45,212,191,0.18)',
        color: '#5eead4', backdropFilter: 'blur(10px)',
      }}
    >
      {busy ? <Loader2 className="animate-spin" style={{ width: 19, height: 19 }} /> : <Camera style={{ width: 19, height: 19 }} strokeWidth={1.9} />}
    </button>,
    document.body,
  );
}
